import {reactFetchJSON} from '@github-ui/verified-fetch'

export interface IssuesBulkUpdateResult {
  failedIssueNodeIds: string[]
}

export interface IssuesBulkUpdateProgress {
  state: 'PENDING' | 'QUEUED' | 'STARTED'
  percentage: number
  failedCount: number
}

export type IssuesBulkJobStatus =
  | {status: 'pending'; progress: IssuesBulkUpdateProgress}
  | {status: 'completed'; result: IssuesBulkUpdateResult}
  | {status: 'failed'; errorMessage: string}
  | {status: 'missing'}

export const ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE =
  'The update timed out, but some changes might have been applied. Refresh the page before trying again.'

export const ISSUES_BULK_UPDATE_TIMEOUT_MS = 5 * 60 * 1000

interface ExecutionError {
  node_id: string
  message: string
}

function isExecutionError(value: unknown): value is ExecutionError {
  return Boolean(
    value &&
    typeof value === 'object' &&
    'node_id' in value &&
    typeof value.node_id === 'string' &&
    'message' in value &&
    typeof value.message === 'string',
  )
}

function jobFromPayload(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== 'object' || !('job' in payload)) {
    throw new Error('Bulk update returned an invalid job response')
  }
  const job = payload.job
  if (!job || typeof job !== 'object') {
    throw new Error('Bulk update returned an invalid job response')
  }
  return job as Record<string, unknown>
}

function failedIssueNodeIds(payload: unknown, submittedIds?: ReadonlySet<string>): string[] {
  const job = jobFromPayload(payload)
  if (!Array.isArray(job.execution_errors)) throw new Error('Bulk update returned an invalid job response')

  const failedIds = new Set<string>()
  for (const executionError of job.execution_errors) {
    if (!isExecutionError(executionError)) {
      throw new Error('Bulk update returned an invalid job response')
    }
    if (submittedIds && !submittedIds.has(executionError.node_id)) {
      throw new Error('Bulk update returned an unexpected failed issue')
    }
    failedIds.add(executionError.node_id)
  }
  return [...failedIds]
}

function jobProgress(payload: unknown): IssuesBulkUpdateProgress {
  const job = jobFromPayload(payload)
  const state = typeof job.state === 'string' ? job.state.toUpperCase() : ''
  if (state !== 'PENDING' && state !== 'QUEUED' && state !== 'STARTED') {
    throw new Error('Bulk update returned an invalid job response')
  }
  return {
    state,
    percentage: typeof job.percentage === 'number' ? job.percentage : 0,
    failedCount: Array.isArray(job.execution_errors) ? job.execution_errors.length : 0,
  }
}

export async function fetchIssuesBulkJobStatus({
  jobId,
  submittedIds,
  signal,
}: {
  jobId: string
  submittedIds?: readonly string[]
  signal?: AbortSignal
}): Promise<IssuesBulkJobStatus> {
  const response = await reactFetchJSON(`/_ghui/item-pickers/bulk_actions/${encodeURIComponent(jobId)}`, {
    headers: {Accept: 'application/json'},
    signal,
  })
  if (response.status === 404 || response.status === 410) return {status: 'missing'}
  if (response.status === 500) {
    const job = jobFromPayload(await response.json())
    return {
      status: 'failed',
      errorMessage: typeof job.error_message === 'string' ? job.error_message : 'Bulk update had an error',
    }
  }
  if (!response.ok) throw new Error(`HTTP ${response.status}`)

  const payload = await response.json()
  if (response.status === 202) return {status: 'pending', progress: jobProgress(payload)}
  return {
    status: 'completed',
    result: {failedIssueNodeIds: failedIssueNodeIds(payload, submittedIds ? new Set(submittedIds) : undefined)},
  }
}
